import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { USER_MANAGEMENT_DASHBOARD, CIRCADIA_USERS } from '../../constants/messages';
import RoundedButtonLink from '../../components/RoundedButton/RoundedButtonLink';
import { userColumns } from '../../components/DataGrid/columnDefinitions';
import { useLazyGetUsersQuery } from '../../api/user.api';
import DataGridProComponent from '../../components/DataGrid/DataGridProComponent';
import { UserAccountsDataGridType } from '../../components/DataGrid/DataGridTypes';
import type { GridDataSource } from '@mui/x-data-grid-pro';
import {ColorBaseCharcoal, ColorBaseGreenEnergy} from "@huntington/honeycomb-design/dist";
import { useLocation } from 'react-router-dom';

const navLinks = {
    addUser: { label: 'Add New User', path: 'add-user' }
};

function UserManagement() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

		const location = useLocation();
		const fetchFromCache = location.state?.fetchUserDataFromCache ?? true;

    const [dataIsLoading, setDataIsLoading] = useState(false);

    const [getUsers] = useLazyGetUsersQuery();

    const usersDataSource: GridDataSource = useMemo(
        () => ({
            getRows: async ({ paginationModel }) => {
                // Refetch with new pagination
                const queryParams = {
                    pageSize: paginationModel?.pageSize ?? 10,
                    currentPage: paginationModel?.page ?? 0,
                };

                setDataIsLoading(true);

                const result = await getUsers(queryParams, fetchFromCache).unwrap();

                setDataIsLoading(false);

                return {
                    rows: result?.content ?? [],
                    rowCount: result?.totalElements ?? 0,
                };
            },
        }),
        []
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{ color: ColorBaseGreenEnergy, fontWeight: 'bold' }}
                >
                    {USER_MANAGEMENT_DASHBOARD}
                </Typography>
            </Box>
            <Typography
                variant="h6"
                sx={{
                    color: ColorBaseCharcoal,
                    width: '100%',
                    borderBottom: `1px solid ${ColorBaseCharcoal}`,
                    pb: 1,
                }}
            >
                {CIRCADIA_USERS}
            </Typography>
            <Box display="flex" justifyContent="flex-end" mt={isMobile ? 1.5 : 3}>
                <RoundedButtonLink
                    navLink={navLinks.addUser}
                    variantStyle="dark"
                    variant="contained"
                />
            </Box>
            <Box sx={{ width: '100%', mt: 3, padding: 0 }}>
                <DataGridProComponent
                    columns={userColumns}
                    dataGridType={UserAccountsDataGridType}
                    dataSource={usersDataSource}
                    dataIsLoading={dataIsLoading}
                    checkboxEnabled={false}
                />
            </Box>
        </Box>
    );
}

export default UserManagement;